import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Plus, Trash2, Pencil, FileDown, Filter, X, CreditCard } from "lucide-react";
import { useConfig, ALL_CONFIG_GROUPS } from "@/hooks/useConfig";
import { fmtDate, istToday } from "@/lib/dateFormat";
import { fmtDateTime } from "@/lib/dateFormat";

const fmt = (n) => new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2 }).format(n || 0);
const SC = { "Available": "bg-green-100 text-green-700", "Partially Used": "bg-amber-100 text-amber-700", "Fully Used": "bg-zinc-200 text-zinc-600", "Open": "bg-green-100 text-green-700", "Pending Approval": "bg-orange-100 text-orange-700", "Rejected": "bg-red-100 text-red-700" };
const STATUS_OPTIONS = ["Pending Approval", "Available", "Partially Used", "Fully Used", "Rejected"];

export default function CreditNotesPage() {
  const { user } = useAuth();
  const perms = user?.permissions || [];
  const userRoles = user?.roles || [];
  const canCreate = perms.some(p => ["admin.full_access", "finance.approve"].includes(p));
  const isAdmin = userRoles.some(r => ["Admin", "admin"].includes(r));
  const canExport = isAdmin || userRoles.some(r => ["Finance Manager", "Finance Executive"].includes(r)) || perms.some(p => ["admin.full_access", "finance.view_all_orders"].includes(p));
  const { getValues } = useConfig(ALL_CONFIG_GROUPS);
  const CN_REASONS = getValues("cn_reasons");

  // Core list state
  const [cns, setCns] = useState([]);
  const [loading, setLoading] = useState(true);
  const pendingCount = cns.filter(cn => cn.status === "Pending Approval").length;

  // Create dialog
  const [showCreate, setShowCreate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ cn_number: "", customer_id: "", customer_name: "", cn_date: istToday(), amount: 0, reason: "", notes: "", source_adjustment_id: "" });
  const [customers, setCustomers] = useState([]);
  const [custQuery, setCustQuery] = useState("");
  const [showCustResults, setShowCustResults] = useState(false);
  const [pendingSSAdj, setPendingSSAdj] = useState([]);
  const [suggestedAmt, setSuggestedAmt] = useState(null);

  // Filters (Part 6)
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ customer_id: "", customer_name: "", status: [], date_from: "", date_to: "", amount_min: "", amount_max: "", raised_by: "" });
  const [filterCustQuery, setFilterCustQuery] = useState("");
  const [filterCustResults, setFilterCustResults] = useState([]);
  const [showFilterCustResults, setShowFilterCustResults] = useState(false);
  const [cnCreators, setCnCreators] = useState([]);
  const activeFilterCount = [filters.customer_id, filters.status.length > 0, filters.date_from, filters.date_to, filters.amount_min, filters.amount_max, filters.raised_by].filter(Boolean).length;

  // Detail view (Part 4)
  const [detailCn, setDetailCn] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  // Edit (Part 3)
  const [editCn, setEditCn] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({ cn_date: "", amount: 0, reason: "", notes: "" });
  const [editSubmitting, setEditSubmitting] = useState(false);

  // Delete (Part 2)
  const [deleteCn, setDeleteCn] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Load CN list with filters
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.customer_id) params.customer_id = filters.customer_id;
      if (filters.status.length > 0) params.status = filters.status.join(",");
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.amount_min) params.amount_min = filters.amount_min;
      if (filters.amount_max) params.amount_max = filters.amount_max;
      if (filters.raised_by) params.raised_by = filters.raised_by;
      const { data } = await api.get("/credit-notes", { params });
      setCns(data);
    } catch (e) { console.error("Load error:", e); } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (canCreate) api.get("/credit-notes/pending-ss-adjustments").then(r => setPendingSSAdj(r.data)).catch(() => {});
    api.get("/credit-notes/cn-creators").then(r => setCnCreators(r.data)).catch(() => {});
  }, []);

  // FIX-UP R3b — opened from a closed RTO: the customer, the suggested amount (editable) and the
  // reason come straight from the link, so no order-number lookup can resolve to the wrong order.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const orderNum = q.get("prefill_order");
    const rtoId = q.get("prefill_rto_id") || "";
    if (!rtoId || !canCreate) return;
    const amount = parseFloat(q.get("prefill_amount") || "0") || 0;
    const reason = q.get("prefill_reason") || "Goods returned";
    const custId = q.get("prefill_customer_id") || "";
    const custName = q.get("prefill_customer_name") || "";
    setForm(f => ({
      ...f, amount, reason: "Other", notes: `${reason} — order ${orderNum || ""}`,
      customer_id: custId, customer_name: custName, rto_id: rtoId,
    }));
    setCustQuery(custName);
    setShowCreate(true);
    toast.info(`Credit note for ${custName || orderNum} — the amount can be changed`);
  }, [canCreate]);

  const searchCust = async (q) => {
    setCustQuery(q);
    if (q.length < 2) return;
    try { const { data } = await api.get("/customers", { params: { search: q } }); setCustomers(data); setShowCustResults(true); } catch (e) { console.error("Error:", e); }
  };

  const searchFilterCust = async (q) => {
    setFilterCustQuery(q);
    if (q.length < 2) { setFilterCustResults([]); return; }
    try { const { data } = await api.get("/customers", { params: { search: q } }); setFilterCustResults(data); setShowFilterCustResults(true); } catch (e) { console.error(e); }
  };

  const handleCreate = async () => {
    if (!form.cn_number || !form.cn_number.trim()) { toast.error("CN Number is required"); return; }
    if (!form.customer_id) { toast.error("Select a customer"); return; }
    if (!form.cn_date) { toast.error("CN Date is required"); return; }
    if (!form.amount || form.amount <= 0) { toast.error("Amount must be greater than 0"); return; }
    if (!form.reason) { toast.error("Reason is required"); return; }
    if (!form.notes || !form.notes.trim()) { toast.error("Notes/description is required"); return; }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.source_adjustment_id) delete payload.source_adjustment_id;
      if (!payload.rto_id) delete payload.rto_id;      /* FIX-UP R3c */
      const { data } = await api.post("/credit-notes", payload);
      toast.success(`Credit Note ${data.cn_number} created`);
      setShowCreate(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  // Detail view
  const openDetail = async (cnId) => {
    setDetailLoading(true); setShowDetail(true);
    try {
      const { data } = await api.get(`/credit-notes/${cnId}`);
      setDetailCn(data);
    } catch (e) { toast.error("Failed to load CN details"); setShowDetail(false); }
    finally { setDetailLoading(false); }
  };

  // Edit
  const openEdit = (cn) => {
    const isApproved = ["Available", "Partially Used", "Fully Used"].includes(cn.status);
    setEditCn(cn);
    setEditForm({ cn_date: cn.cn_date || "", amount: cn.amount || 0, reason: cn.reason || "", notes: cn.notes || "", _isApproved: isApproved });
    setShowEdit(true);
  };

  const handleEdit = async () => {
    if (!editCn) return;
    setEditSubmitting(true);
    try {
      const payload = {};
      if (editForm.cn_date) payload.cn_date = editForm.cn_date;
      if (!editForm._isApproved && editForm.amount > 0) payload.amount = editForm.amount;
      if (editForm.reason) payload.reason = editForm.reason;
      if (editForm.notes) payload.notes = editForm.notes;
      await api.put(`/credit-notes/${editCn._id}`, payload);
      toast.success("Credit note updated");
      setShowEdit(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setEditSubmitting(false); }
  };

  // Delete
  const handleDelete = async () => {
    if (!deleteCn) return;
    try {
      await api.delete(`/credit-notes/${deleteCn._id}`);
      toast.success(`CN ${deleteCn.cn_number} deleted`);
      setShowDeleteConfirm(false); setDeleteCn(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Export
  const handleExport = async () => {
    try {
      const params = {};
      if (filters.customer_id) params.customer_id = filters.customer_id;
      if (filters.status.length > 0) params.status = filters.status.join(",");
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.amount_min) params.amount_min = filters.amount_min;
      if (filters.amount_max) params.amount_max = filters.amount_max;
      if (filters.raised_by) params.raised_by = filters.raised_by;
      const resp = await api.get("/credit-notes/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a"); a.href = url; a.download = "Credit_Notes_Export.xlsx"; a.click();
      toast.success("Export downloaded");
    } catch (e) { toast.error("Export failed"); }
  };

  const clearFilters = () => {
    setFilters({ customer_id: "", customer_name: "", status: [], date_from: "", date_to: "", amount_min: "", amount_max: "", raised_by: "" });
    setFilterCustQuery("");
  };

  const toggleStatus = (s) => {
    setFilters(prev => ({ ...prev, status: prev.status.includes(s) ? prev.status.filter(x => x !== s) : [...prev.status, s] }));
  };

  const canEditCn = (cn) => {
    if (isAdmin) return true;
    if (cn.created_by === user?.id && ["Pending Approval", "Rejected"].includes(cn.status)) return true;
    return false;
  };

  const isDeletable = (cn) => cn.amount_used <= 0;

  return (
    <div data-testid="credit-notes-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">Credit Notes</h1>
          {pendingCount > 0 && <span className="px-2 py-0.5 text-xs font-semibold bg-orange-100 text-orange-700 rounded-full cursor-pointer" onClick={() => setFilters(p => ({ ...p, status: ["Pending Approval"] }))} data-testid="pending-badge">Pending ({pendingCount})</span>}
          <p className="text-sm text-zinc-500 mt-1">Manage credit notes issued to customers</p>
        </div>
        <div className="flex items-center gap-2">
          {canExport && <Button variant="outline" onClick={handleExport} className="rounded-sm h-8 text-xs" data-testid="export-cn-btn"><FileDown size={14} className="mr-1" />Export to Excel</Button>}
          {canCreate && <Button onClick={() => { setForm({ cn_number: "", customer_id: "", customer_name: "", cn_date: istToday(), amount: 0, reason: "", notes: "" }); setCustQuery(""); setShowCreate(true); }} className="rounded-sm h-8 text-xs" data-testid="create-cn-btn"><Plus size={14} className="mr-1" />Create Credit Note</Button>}
        </div>
      </div>

      {/* Filter Bar */}
      <div className="flex gap-3 mb-4 flex-wrap items-center">
        <Button variant="outline" size="sm" className="h-8 text-xs rounded-sm relative" onClick={() => setShowFilters(!showFilters)} data-testid="filter-toggle-btn">
          <Filter size={14} className="mr-1" />Filters
          {activeFilterCount > 0 && <span className="absolute -top-1.5 -right-1.5 bg-blue-600 text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center" data-testid="active-filter-badge">{activeFilterCount}</span>}
        </Button>
        {activeFilterCount > 0 && <Button variant="ghost" size="sm" className="h-8 text-xs text-zinc-500" onClick={clearFilters} data-testid="clear-filters-btn"><X size={12} className="mr-1" />Clear filters</Button>}
        {/* Quick status pills */}
        {filters.status.map(s => <span key={s} className="px-2 py-0.5 text-[10px] font-medium bg-blue-50 text-blue-700 rounded-full flex items-center gap-1">{s}<button onClick={() => toggleStatus(s)} className="hover:text-red-500"><X size={10} /></button></span>)}
        {filters.customer_name && <span className="px-2 py-0.5 text-[10px] font-medium bg-blue-50 text-blue-700 rounded-full flex items-center gap-1">{filters.customer_name}<button onClick={() => setFilters(p => ({ ...p, customer_id: "", customer_name: "" }))} className="hover:text-red-500"><X size={10} /></button></span>}
      </div>

      {/* Expandable Filter Panel */}
      {showFilters && (
        <div className="mb-4 p-4 bg-zinc-50 border border-zinc-200 rounded-sm space-y-3" data-testid="filter-panel">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {/* Customer search */}
            <div className="relative">
              <Label className="text-[10px] uppercase text-zinc-500 font-semibold">Customer</Label>
              {filters.customer_id ? (
                <div className="mt-1 flex items-center gap-1"><span className="text-xs bg-blue-50 border border-blue-200 px-2 py-1 rounded-sm flex-1 truncate">{filters.customer_name}</span><button onClick={() => { setFilters(p => ({ ...p, customer_id: "", customer_name: "" })); setFilterCustQuery(""); }} className="text-zinc-400 hover:text-red-500"><X size={14} /></button></div>
              ) : (
                <>
                  <Input value={filterCustQuery} onChange={e => searchFilterCust(e.target.value)} className="mt-1 rounded-sm text-xs h-8" placeholder="Search customer..." />
                  {showFilterCustResults && filterCustResults.length > 0 && <div className="absolute z-50 mt-1 w-full bg-white border border-zinc-200 rounded-sm shadow-lg max-h-36 overflow-y-auto">{filterCustResults.map(c => <button key={c._id} onClick={() => { setFilters(p => ({ ...p, customer_id: c._id, customer_name: c.company_name || c.name })); setShowFilterCustResults(false); setFilterCustQuery(""); }} className="w-full text-left px-3 py-2 text-xs hover:bg-zinc-50 border-b border-zinc-50">{c.company_name || c.name}</button>)}</div>}
                </>
              )}
            </div>
            {/* Raised by */}
            <div>
              <Label className="text-[10px] uppercase text-zinc-500 font-semibold">Raised By</Label>
              <Select value={filters.raised_by || "all"} onValueChange={v => setFilters(p => ({ ...p, raised_by: v === "all" ? "" : v }))}>
                <SelectTrigger className="mt-1 h-8 text-xs rounded-sm"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>{[{ user_id: "all", name: "All" }, ...cnCreators].map(c => <SelectItem key={c.user_id} value={c.user_id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {/* Date range */}
            <div>
              <Label className="text-[10px] uppercase text-zinc-500 font-semibold">Date From</Label>
              <Input type="date" value={filters.date_from} onChange={e => setFilters(p => ({ ...p, date_from: e.target.value }))} className="mt-1 rounded-sm text-xs h-8" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-zinc-500 font-semibold">Date To</Label>
              <Input type="date" value={filters.date_to} onChange={e => setFilters(p => ({ ...p, date_to: e.target.value }))} className="mt-1 rounded-sm text-xs h-8" />
            </div>
            {/* Amount range */}
            <div>
              <Label className="text-[10px] uppercase text-zinc-500 font-semibold">Min Amount (₹)</Label>
              <Input type="number" value={filters.amount_min} onChange={e => setFilters(p => ({ ...p, amount_min: e.target.value }))} className="mt-1 rounded-sm text-xs h-8" placeholder="0" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-zinc-500 font-semibold">Max Amount (₹)</Label>
              <Input type="number" value={filters.amount_max} onChange={e => setFilters(p => ({ ...p, amount_max: e.target.value }))} className="mt-1 rounded-sm text-xs h-8" placeholder="∞" />
            </div>
          </div>
          {/* Status multi-select */}
          <div>
            <Label className="text-[10px] uppercase text-zinc-500 font-semibold mb-1 block">Status</Label>
            <div className="flex flex-wrap gap-1.5">
              {STATUS_OPTIONS.map(s => (
                <button key={s} onClick={() => toggleStatus(s)} className={`px-2 py-1 text-[10px] font-medium rounded-sm border transition-colors ${filters.status.includes(s) ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"}`} data-testid={`filter-status-${s.replace(/\s/g, "-").toLowerCase()}`}>{s}</button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Pending SS Adjustments */}
      {canCreate && pendingSSAdj.length > 0 && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-sm" data-testid="pending-ss-adj-panel">
          <h3 className="text-xs font-semibold text-amber-800 mb-2">Pending SS Adjustments — Needs a Credit Note ({pendingSSAdj.length})</h3>
          {pendingSSAdj.map(a => (
            <div key={a._id} className="flex items-center justify-between py-1 border-b border-amber-100 last:border-0">
              <div className="text-[10px]"><span className="font-medium">{a.ss_name}</span> — {a.sku_name} ({a.difference > 0 ? "+" : ""}{a.difference} units)</div>
              <Button size="sm" className="h-6 text-[10px] rounded-sm" onClick={async () => {
                setSuggestedAmt(null);
                try {
                  const { data: sug } = await api.get(`/credit-notes/suggest-amount/${a._id}`);
                  setSuggestedAmt(sug);
                  setForm({ cn_number: "", customer_id: a.ss_customer_id, customer_name: a.ss_name, cn_date: istToday(), amount: sug.suggested_amount || 0, reason: `Stock adjustment: ${a.reason_category}`, notes: `${a.reason_text || ""} | Adjustment for ${a.sku_name} batch ${a.batch_number} (${a.difference > 0 ? "+" : ""}${a.difference})`, source_adjustment_id: a._id });
                } catch (e) {
                  setForm({ cn_number: "", customer_id: a.ss_customer_id, customer_name: a.ss_name, cn_date: istToday(), amount: 0, reason: `Stock adjustment: ${a.reason_category}`, notes: a.reason_text || "", source_adjustment_id: a._id });
                }
                setCustQuery(a.ss_name); setShowCreate(true);
              }}>Create CN</Button>
            </div>
          ))}
        </div>
      )}

      {/* CN Table */}
      <div className="border border-zinc-200 overflow-x-auto" data-testid="cn-table">
        <table className="data-table w-full">
          <thead><tr><th>CN Number</th><th>Customer</th><th>Date</th><th>Reason</th><th>Notes</th><th className="num">Amount</th><th className="num">Used</th><th className="num">Balance</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={10} className="text-center py-8 text-zinc-400 text-xs">Loading...</td></tr> :
            cns.length === 0 ? <tr><td colSpan={10} className="text-center py-8 text-zinc-400 text-xs">No credit notes found</td></tr> :
            cns.map(cn => (
              <tr key={cn._id} data-testid={`cn-row-${cn._id}`} className="cursor-pointer hover:bg-zinc-50" onClick={(e) => { if (e.target.closest("[data-action-zone]")) return; openDetail(cn._id); }}>
                <td className="font-mono text-xs font-semibold">{cn.cn_number}</td>
                <td className="text-xs font-medium">{cn.customer_name}</td>
                <td className="text-xs text-zinc-500">{fmtDate(cn.cn_date)}</td>
                <td className="text-xs">{cn.reason}</td>
                <td className="text-xs text-zinc-500 truncate max-w-[120px]">{cn.notes || "—"}</td>
                <td className="num font-mono text-xs">₹{fmt(cn.amount)}</td>
                <td className="num font-mono text-xs text-amber-600">₹{fmt(cn.amount_used)}</td>
                <td className="num font-mono text-xs font-semibold">₹{fmt(cn.balance)}</td>
                <td><span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${SC[cn.status] || ''}`}>{cn.status}</span>
                  {cn.rejection_reason && <p className="text-[8px] text-red-500 mt-0.5">Rejected: {cn.rejection_reason}</p>}
                </td>
                <td data-action-zone="true" className="text-xs">
                  <div className="flex items-center gap-1">
                    {isAdmin && cn.status === "Pending Approval" && <>
                      <Button size="sm" variant="outline" className="h-5 text-[9px] px-1 border-green-300 text-green-700" data-testid={`approve-cn-${cn._id}`} onClick={async () => { try { await api.post(`/credit-notes/${cn._id}/approve`); toast.success("CN approved"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } }}>Approve</Button>
                      <Button size="sm" variant="outline" className="h-5 text-[9px] px-1 border-red-300 text-red-700" data-testid={`reject-cn-${cn._id}`} onClick={() => { const reason = prompt("Rejection reason (min 10 chars):"); if (reason && reason.length >= 10) { api.post(`/credit-notes/${cn._id}/reject`, { rejection_reason: reason }).then(() => { toast.success("CN rejected"); load(); }).catch(e => toast.error(formatApiError(e.response?.data?.detail))); } else if (reason) { toast.error("Reason must be at least 10 characters"); } }}>Reject</Button>
                    </>}
                    {canCreate && cn.status === "Rejected" && <Button size="sm" variant="outline" className="h-5 text-[9px] px-1 border-orange-300 text-orange-700" onClick={() => { setForm({ cn_number: cn.cn_number, customer_id: cn.customer_id, customer_name: cn.customer_name, cn_date: cn.cn_date, amount: cn.amount, reason: cn.reason, notes: cn.notes || "" }); setShowCreate(true); }}>Resubmit</Button>}
                    {/* Edit icon */}
                    {canEditCn(cn) && (
                      <TooltipProvider><Tooltip><TooltipTrigger asChild>
                        <button onClick={() => openEdit(cn)} className="p-1 rounded-sm hover:bg-zinc-100 text-zinc-500 hover:text-blue-600 transition-colors" data-testid={`edit-cn-${cn._id}`}><Pencil size={13} /></button>
                      </TooltipTrigger><TooltipContent side="top" className="text-[10px]">Edit</TooltipContent></Tooltip></TooltipProvider>
                    )}
                    {/* Delete icon */}
                    {isAdmin && (
                      <TooltipProvider><Tooltip><TooltipTrigger asChild>
                        <button onClick={() => { if (isDeletable(cn)) { setDeleteCn(cn); setShowDeleteConfirm(true); } }} className={`p-1 rounded-sm transition-colors ${isDeletable(cn) ? "hover:bg-red-50 text-zinc-400 hover:text-red-600" : "text-zinc-200 cursor-not-allowed"}`} disabled={!isDeletable(cn)} data-testid={`delete-cn-${cn._id}`}><Trash2 size={13} /></button>
                      </TooltipTrigger><TooltipContent side="top" className="text-[10px]">{isDeletable(cn) ? "Delete" : "Cannot delete — CN has been applied to orders"}</TooltipContent></Tooltip></TooltipProvider>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ═══ CREATE CN DIALOG ═══ */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg" data-testid="create-cn-dialog">
          <DialogHeader><DialogTitle className="font-heading">Create Credit Note</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">CN Number (from Tally) *</Label><Input value={form.cn_number} onChange={e => setForm({ ...form, cn_number: e.target.value })} className="mt-1 rounded-sm text-xs" placeholder="e.g. CN/2025-26/001" data-testid="cn-number-input" /></div>
            <div className="relative">
              <Label className="text-xs">Customer *</Label>
              {form.customer_id ? (
                <div className="mt-1 flex items-center gap-2"><span className="text-xs bg-blue-50 border border-blue-200 px-2 py-1 rounded-sm flex-1">{form.customer_name}</span><Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setForm({ ...form, customer_id: "", customer_name: "" })}>×</Button></div>
              ) : (
                <><Input value={custQuery} onChange={e => searchCust(e.target.value)} className="mt-1 rounded-sm text-xs" placeholder="Search customer..." />
                {showCustResults && customers.length > 0 && <div className="absolute z-50 mt-1 w-full bg-white border border-zinc-200 rounded-sm shadow-lg max-h-36 overflow-y-auto">{customers.map(c => <button key={c._id} onClick={() => { setForm({ ...form, customer_id: c._id, customer_name: c.company_name || c.name, channel_id: c.channel_id || "", channel_name: "" }); setShowCustResults(false); }} className="w-full text-left px-3 py-2 text-xs hover:bg-zinc-50 border-b border-zinc-50">{c.company_name || c.name}</button>)}</div>}</>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">CN Date</Label><Input type="date" value={form.cn_date} onChange={e => setForm({ ...form, cn_date: e.target.value })} className="mt-1 rounded-sm text-xs" /></div>
              <div><Label className="text-xs">Amount (₹) *</Label><Input type="number" step="0.01" value={form.amount || ""} onChange={e => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} className="mt-1 rounded-sm text-xs" />{suggestedAmt && suggestedAmt.suggested_amount && <p className="text-[10px] text-blue-600 mt-1">Suggested: ₹{suggestedAmt.suggested_amount} ({suggestedAmt.breakdown})</p>}{suggestedAmt && !suggestedAmt.suggested_amount && <p className="text-[10px] text-amber-600 mt-1">{suggestedAmt.reason}</p>}</div>
            </div>
            <div><Label className="text-xs">Reason *</Label>
              <Select value={form.reason || "none"} onValueChange={v => setForm({ ...form, reason: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1 rounded-sm text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent><SelectItem value="none">-- Select --</SelectItem>{CN_REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Notes *</Label><Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-sm text-xs" rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="rounded-sm text-xs">Cancel</Button>
            <Button onClick={handleCreate} disabled={submitting} className="rounded-sm text-xs" data-testid="save-cn-btn">{submitting ? "Creating..." : "Create Credit Note"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ EDIT CN SHEET ═══ */}
      <Sheet open={showEdit} onOpenChange={setShowEdit}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="edit-cn-sheet">
          <SheetHeader>
            <SheetTitle className="font-heading">Edit Credit Note</SheetTitle>
            <SheetDescription>{editCn?.cn_number} — {editCn?.customer_name}</SheetDescription>
          </SheetHeader>
          <div className="space-y-4 mt-6">
            <div><Label className="text-xs">CN Date</Label><Input type="date" value={editForm.cn_date} onChange={e => setEditForm(p => ({ ...p, cn_date: e.target.value }))} className="mt-1 rounded-sm text-xs" /></div>
            {!editForm._isApproved && (
              <div><Label className="text-xs">Amount (₹)</Label><Input type="number" step="0.01" value={editForm.amount || ""} onChange={e => setEditForm(p => ({ ...p, amount: parseFloat(e.target.value) || 0 }))} className="mt-1 rounded-sm text-xs" /></div>
            )}
            {editForm._isApproved && (
              <div><Label className="text-xs text-zinc-400">Amount (₹) — locked after approval</Label><Input disabled value={editCn?.amount || 0} className="mt-1 rounded-sm text-xs bg-zinc-50" /></div>
            )}
            <div><Label className="text-xs">Reason</Label>
              <Select value={editForm.reason || "none"} onValueChange={v => setEditForm(p => ({ ...p, reason: v === "none" ? "" : v }))}>
                <SelectTrigger className="mt-1 rounded-sm text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent><SelectItem value="none">-- Select --</SelectItem>{CN_REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Notes</Label><Textarea value={editForm.notes} onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))} className="mt-1 rounded-sm text-xs" rows={3} /></div>
            {!editForm._isApproved && !isAdmin && <p className="text-[10px] text-amber-600 bg-amber-50 p-2 rounded-sm">Saving will reset status to "Pending Approval" and notify Admin.</p>}
            <div className="flex gap-2 pt-2">
              <Button onClick={handleEdit} disabled={editSubmitting} className="rounded-sm text-xs flex-1" data-testid="save-edit-cn-btn">{editSubmitting ? "Saving..." : "Save Changes"}</Button>
              <Button variant="outline" onClick={() => setShowEdit(false)} className="rounded-sm text-xs">Cancel</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* ═══ DELETE CONFIRMATION ═══ */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent data-testid="delete-cn-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Credit Note?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <span className="font-semibold">{deleteCn?.cn_number}</span> (₹{fmt(deleteCn?.amount)}) for {deleteCn?.customer_name}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-xs rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-xs rounded-sm" data-testid="confirm-delete-cn-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ═══ DETAIL VIEW SHEET ═══ */}
      <Sheet open={showDetail} onOpenChange={setShowDetail}>
        <SheetContent side="right" className="w-full sm:max-w-[40vw] overflow-y-auto" data-testid="cn-detail-sheet">
          <SheetHeader>
            <SheetTitle className="font-heading">{detailCn?.cn_number || "Credit Note"}</SheetTitle>
            <SheetDescription>{detailCn?.customer_name}</SheetDescription>
          </SheetHeader>
          {detailLoading ? <p className="text-xs text-zinc-400 mt-8 text-center">Loading...</p> : detailCn && (
            <div className="space-y-5 mt-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-3">
                <InfoField label="CN Number" value={detailCn.cn_number} />
                <InfoField label="CN Date" value={fmtDate(detailCn.cn_date)} />
                <InfoField label="Customer" value={detailCn.customer_name} />
                <InfoField label="Status" value={<span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${SC[detailCn.status] || ''}`}>{detailCn.status}</span>} />
              </div>
              {/* Financial */}
              <div className="grid grid-cols-3 gap-3 p-3 bg-zinc-50 rounded-sm">
                <InfoField label="Original Amount" value={`₹${fmt(detailCn.amount)}`} />
                <InfoField label="Amount Used" value={<span className="text-amber-600">₹{fmt(detailCn.amount_used)}</span>} />
                <InfoField label="Balance" value={<span className="font-semibold">₹{fmt(detailCn.balance)}</span>} />
              </div>
              {/* Reason & Notes */}
              <div className="space-y-2">
                <InfoField label="Reason" value={detailCn.reason || "—"} />
                <InfoField label="Notes" value={detailCn.notes || "—"} />
              </div>
              {/* Workflow */}
              <div className="grid grid-cols-2 gap-3">
                <InfoField label="Raised By" value={detailCn.created_by_name || "—"} />
                <InfoField label="Raised At" value={detailCn.created_at ? `${detailCn.created_at.slice(0, 10)} ${detailCn.created_at.slice(11, 19)}` : "—"} />
                {detailCn.approved_by_name && <InfoField label="Approved By" value={detailCn.approved_by_name} />}
                {detailCn.approved_at && <InfoField label="Approved At" value={detailCn.approved_at.slice(0, 10)} />}
                {detailCn.rejected_by_name && <InfoField label="Rejected By" value={detailCn.rejected_by_name} />}
                {detailCn.rejected_at && <InfoField label="Rejected At" value={detailCn.rejected_at.slice(0, 10)} />}
                {detailCn.rejection_reason && <div className="col-span-2"><InfoField label="Rejection Reason" value={<span className="text-red-600">{detailCn.rejection_reason}</span>} /></div>}
              </div>
              {/* Attachments */}
              {(detailCn.attachments || []).length > 0 && (
                <div>
                  <p className="text-[10px] uppercase text-zinc-500 font-semibold mb-1">Attachments ({detailCn.attachments.length})</p>
                  {detailCn.attachments.map((att, i) => <div key={i} className="text-xs text-blue-600 py-0.5">{att.filename}</div>)}
                </div>
              )}
              {/* Applications History */}
              {(detailCn.applications || []).length > 0 && (
                <div>
                  <p className="text-[10px] uppercase text-zinc-500 font-semibold mb-1">Applications ({detailCn.applications.length})</p>
                  <div className="border border-zinc-200 rounded-sm overflow-hidden">
                    <table className="w-full text-xs">
                      <thead><tr className="bg-zinc-50"><th className="px-2 py-1 text-left text-[10px] font-semibold">Order #</th><th className="px-2 py-1 text-right text-[10px] font-semibold">Amount</th><th className="px-2 py-1 text-left text-[10px] font-semibold">Date</th><th className="px-2 py-1 text-left text-[10px] font-semibold">By</th></tr></thead>
                      <tbody>
                        {detailCn.applications.map((app, i) => (
                          <tr key={i} className="border-t border-zinc-100">
                            <td className="px-2 py-1 font-mono">{app.order_number || app.order_id}</td>
                            <td className="px-2 py-1 text-right font-mono">₹{fmt(app.amount_paise ? app.amount_paise / 100 : app.amount)}</td>
                            <td className="px-2 py-1 text-zinc-500">{(app.applied_at || "").slice(0, 10)}</td>
                            <td className="px-2 py-1 text-zinc-500">{app.applied_by || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {/* Audit Trail */}
              {(detailCn.audit_trail || []).length > 0 && (
                <div>
                  <p className="text-[10px] uppercase text-zinc-500 font-semibold mb-1">Audit Trail</p>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {detailCn.audit_trail.map((a, i) => (
                      <div key={i} className="text-[10px] text-zinc-500 py-1 border-b border-zinc-50">
                        <span className="font-medium text-zinc-700">{a.action || a.event}</span> by {a.user_name || a.performed_by || "—"} — {fmtDateTime(a.timestamp || a.created_at)}
                        {a.new_value && <span className="ml-1 text-zinc-400">({a.new_value})</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function InfoField({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase text-zinc-500 font-semibold">{label}</p>
      <div className="text-xs text-zinc-900 mt-0.5">{value}</div>
    </div>
  );
}
